// src/store/api/propertyPhotoApi.ts - Fixed cache invalidation
import {baseApi} from "./baseApi";
import type {
  PhotoResponseDto,
  PhotoUploadResponseDto,
  PaginatedPhotoResponseDto,
  PhotoFilterDto,
  UploadPhotoDto,
  UpdatePhotoDto,
  ReorderPhotosDto,
  ReorderResponseDto,
  SetPrimaryResponseDto,
  BulkUploadDto,
} from "../../types/property-photo";

export const propertyPhotoApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Upload photos to a property
    uploadPropertyPhotos: builder.mutation<
      PhotoUploadResponseDto,
      {
        propertyId: number;
        files: File[];
        data?: {
          isPrimary?: boolean;
          sortOrder?: number;
        };
      }
    >({
      query: ({propertyId, files, data = {}}) => {
        const formData = new FormData();

        // Backend expects 'photos' field name
        files.forEach((file) => {
          formData.append("photos", file);
        });

        // Add form fields
        if (data.isPrimary !== undefined) {
          formData.append("isPrimary", data.isPrimary.toString());
        }
        if (data.sortOrder !== undefined) {
          formData.append("sortOrder", data.sortOrder.toString());
        }

        return {
          url: `/property-photos/properties/${propertyId}/photos`,
          method: "POST",
          body: formData,
        };
      },
      // ✅ Enhanced cache invalidation - ထုတ်ကုန် property list ကိုပါ update ဖြစ်စေမယ်
      invalidatesTags: (result, error, {propertyId}) => [
        // Property photo specific tags
        {type: "PropertyPhoto", id: `PROPERTY_${propertyId}`},
        {type: "PropertyPhoto", id: "LIST"},
        // Property data itself (အရေးကြီးဆုံး - ဒါက photo count update ဖြစ်စေမယ်)
        {type: "Property", id: propertyId},
        {type: "Property", id: "LIST"}, // ✅ Property list invalidation
        // General photo stats
        "PropertyPhoto",
        "Property", // ✅ All property data invalidation
      ],
    }),

    // Get photos for a property
    getPropertyPhotos: builder.query<PhotoResponseDto[], {propertyId: number}>({
      query: ({propertyId}) => ({
        url: `/property-photos/properties/${propertyId}/photos`,
      }),
      providesTags: (result, error, {propertyId}) => [
        {type: "PropertyPhoto", id: `PROPERTY_${propertyId}`},
        {type: "PropertyPhoto", id: "LIST"},
        ...(result || []).map((photo) => ({
          type: "PropertyPhoto" as const,
          id: photo.id,
        })),
      ],
    }),

    // Get all photos with filters (paginated)
    getAllPropertyPhotos: builder.query<
      PaginatedPhotoResponseDto,
      PhotoFilterDto
    >({
      query: (params) => ({
        url: "/property-photos",
        params,
      }),
      providesTags: (result) => [
        "PropertyPhoto",
        {type: "PropertyPhoto", id: "LIST"},
        ...(result?.data || []).map((photo) => ({
          type: "PropertyPhoto" as const,
          id: photo.id,
        })),
      ],
    }),

    // Get photo by ID
    getPropertyPhotoById: builder.query<PhotoResponseDto, number>({
      query: (id) => `/property-photos/${id}`,
      providesTags: (result, error, id) => [{type: "PropertyPhoto", id}],
    }),

    // Update photo
    updatePropertyPhoto: builder.mutation<
      PhotoResponseDto,
      {id: number; data: UpdatePhotoDto}
    >({
      query: ({id, data}) => ({
        url: `/property-photos/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => {
        const tags: any[] = [
          {type: "PropertyPhoto", id},
          {type: "PropertyPhoto", id: "LIST"},
        ];

        // Only add property-specific tags if we have the propertyId
        if (result?.propertyId) {
          tags.push(
            {type: "PropertyPhoto", id: `PROPERTY_${result.propertyId}`},
            {type: "Property", id: result.propertyId}
          );
        }

        return tags;
      },
    }),

    // Delete photo
    deletePropertyPhoto: builder.mutation<void, number>({
      query: (id) => ({
        url: `/property-photos/${id}`,
        method: "DELETE",
      }),
      // ✅ Enhanced invalidation for delete - property list ကိုပါ update ဖြစ်စေမယ်
      invalidatesTags: (result, error, id) => [
        {type: "PropertyPhoto", id},
        "PropertyPhoto",
        {type: "PropertyPhoto", id: "LIST"},
        // ✅ Property data invalidation (photo count update အတွက်)
        "Property", // All properties
        {type: "Property", id: "LIST"}, // Property list
      ],
    }),

    // Reorder photos
    reorderPropertyPhotos: builder.mutation<
      ReorderResponseDto,
      {propertyId: number; data: ReorderPhotosDto}
    >({
      query: ({propertyId, data}) => ({
        url: `/property-photos/properties/${propertyId}/photos/reorder`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, {propertyId}) => [
        {type: "PropertyPhoto", id: `PROPERTY_${propertyId}`},
        {type: "PropertyPhoto", id: "LIST"},
      ],
    }),

    // Set primary photo
    setPrimaryPhoto: builder.mutation<SetPrimaryResponseDto, {photoId: number}>(
      {
        query: ({photoId}) => ({
          url: `/property-photos/${photoId}/set-primary`,
          method: "POST",
          body: {},
        }),
        invalidatesTags: (result, error, {photoId}) => {
          const tags: any[] = [
            {type: "PropertyPhoto", id: photoId},
            {type: "PropertyPhoto", id: "LIST"},
          ];

          // Only add property-specific tags if we have the propertyId
          if (result?.primaryPhoto?.propertyId) {
            tags.push(
              {
                type: "PropertyPhoto",
                id: `PROPERTY_${result.primaryPhoto.propertyId}`,
              },
              {type: "Property", id: result.primaryPhoto.propertyId}
            );
          }

          return tags;
        },
      }
    ),

    // Get photo statistics
    getPhotoStats: builder.query<
      {
        totalPhotos: number;
        propertiesWithPhotos: number;
        averagePhotosPerProperty: number;
        maxPhotosPerProperty: number;
        propertiesWithoutPhotos: number;
        primaryPhotosCount: number;
        totalStorageUsed: string;
        averagePhotoSize: string;
        popularDimensions: string;
      },
      void
    >({
      query: () => "/property-photos/stats",
      providesTags: ["PropertyPhoto"],
    }),

    // Bulk delete photos
    bulkDeletePhotos: builder.mutation<
      {message: string; deletedCount: number},
      {photoIds: number[]}
    >({
      query: (data) => ({
        url: "/property-photos/bulk/delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["PropertyPhoto", "Property"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadPropertyPhotosMutation,
  useGetPropertyPhotosQuery,
  useGetAllPropertyPhotosQuery,
  useGetPropertyPhotoByIdQuery,
  useUpdatePropertyPhotoMutation,
  useDeletePropertyPhotoMutation,
  useReorderPropertyPhotosMutation,
  useSetPrimaryPhotoMutation,
  useGetPhotoStatsQuery,
  useBulkDeletePhotosMutation,
} = propertyPhotoApi;
